import React, { useState } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Slider,
  Button,
  Stack,
} from '@mui/material';
import { Star, SlidersHorizontal } from 'lucide-react';
import { GENERAL_AMENITIES, RECREATIONAL_AMENITIES, DINING_AMENITIES, SERVICE_AMENITIES } from '../types/property';

const BLUE = '#0066cc';

const sectionTitleSx = {
  fontSize: 12,
  fontWeight: 800,
  color: '#0f172a',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  mb: 1.5,
};

const checkboxRowSx = {
  m: 0,
  px: 0.5,
  borderRadius: '12px',
  transition: 'background-color 0.2s, color 0.2s',
  '&:hover': { bgcolor: 'rgba(239,246,255,0.8)', color: BLUE },
  '& .MuiFormControlLabel-label': {
    fontSize: 13,
    fontWeight: 600,
    color: 'inherit',
    textTransform: 'capitalize',
  },
};

const checkboxSx = {
  p: 0.75,
  color: '#cbd5e1',
  '&.Mui-checked': { color: BLUE },
  '& .MuiSvgIcon-root': { fontSize: 20 },
};

export function FilterSidebar({ filters, onFilterChange }) {
  const propertyTypes = ['hotel', 'homestay', 'resort', 'farm stay', 'villa', 'cottage', 'hostel', 'dormitory'];
  const amenitiesList = [
  ...GENERAL_AMENITIES,
  ...RECREATIONAL_AMENITIES,
  ...DINING_AMENITIES,
  ...SERVICE_AMENITIES,
];

const [showAllAmenities, setShowAllAmenities] = useState(false);

const visibleAmenities = showAllAmenities
  ? amenitiesList
  : amenitiesList.slice(0, 8); // show first 6 only

const handlePropertyTypeChange = (type) => {
  const newTypes = filters.propertyTypes.includes(type)
    ? filters.propertyTypes.filter(t => t !== type)
    : [...filters.propertyTypes, type];
  onFilterChange({ ...filters, propertyTypes: newTypes });
};


  const handleAmenityChange = (amenity, checked) => {
    const updated = checked
      ? [...filters.amenities, amenity]
      : filters.amenities.filter(a => a !== amenity);
    onFilterChange({ ...filters, amenities: updated });
  };

  const handlePriceChange = (event, newValue) => {
    onFilterChange({ ...filters, priceRange: newValue });
  };

  const handleRatingChange = (rating) => {
    onFilterChange({ ...filters, ratings: filters.ratings === rating ? 0 : rating });
  };

  const clearFilters = () => {
    onFilterChange({
      propertyTypes: [],
      priceRange: [0, 100000],
      amenities: [],
      ratings: 0,
    });
  };

  return (
<Box
  sx={{
    bgcolor: 'background.paper',
    borderRadius: '24px',
    border: '1px solid rgba(219,234,254,0.8)',
    p: 3,
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    position: { xs: 'relative', md: 'sticky' },
    top: { md: 96 }, // only desktop
    height: { xs: '100%', md: 'fit-content' }, // important for drawer
    overflowY: { xs: 'auto', md: 'visible' }, // scroll inside mobile drawer
  }}
>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        pb={1.5}
        sx={{ borderBottom: '1px solid #f1f5f9' }}
      >
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 16, fontWeight: 800, color: '#0f172a' }}
        >
          <SlidersHorizontal size={18} color={BLUE} /> Filters
        </Typography>
        <Button
          variant="text"
          size="small"
          onClick={clearFilters}
          sx={{
            color: BLUE,
            fontSize: 11,
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            minWidth: 0,
            '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
          }}
        >
          Clear all
        </Button>
      </Stack>

      {/* Property Type */}
<Box mb={3}>
  <Typography sx={sectionTitleSx}>Property Type</Typography>
  <Stack spacing={0.25}>
    {propertyTypes.map((type) => (
      <FormControlLabel
        key={type}
        sx={checkboxRowSx}
        control={
          <Checkbox
            sx={checkboxSx}
            checked={filters.propertyTypes.includes(type)}
            onChange={e => handlePropertyTypeChange(type)}
          />
        }
        label={type}
      />
    ))}
  </Stack>
</Box>


      {/* Price Range */}
      <Box mb={3} pt={3} sx={{ borderTop: '1px solid #f1f5f9' }}>
        <Typography sx={sectionTitleSx}>Price Range</Typography>
 <Slider
  value={filters.priceRange}
  onChange={handlePriceChange}
  min={0}
  max={100000}
  step={500}
  valueLabelDisplay="auto"
  sx={{
    color: BLUE,
    '& .MuiSlider-rail': { bgcolor: '#e2e8f0', opacity: 1 },
    '& .MuiSlider-thumb': { boxShadow: '0 2px 6px rgba(0,102,204,0.35)' },
    '& .MuiSlider-valueLabel': { bgcolor: '#002b49', borderRadius: '8px' },
  }}
/>

        <Stack direction="row" justifyContent="space-between" fontSize="0.75rem" fontWeight={700}>
          <Box component="span" sx={{ color: BLUE, bgcolor: '#eff6ff', border: '1px solid #dbeafe', px: 1, py: 0.25, borderRadius: '6px' }}>
            ₹{filters.priceRange[0].toLocaleString('en-IN')}
          </Box>
          <Box component="span" sx={{ color: BLUE, bgcolor: '#eff6ff', border: '1px solid #dbeafe', px: 1, py: 0.25, borderRadius: '6px' }}>
            ₹{filters.priceRange[1].toLocaleString('en-IN')}
          </Box>
        </Stack>
      </Box>



      {/* Amenities */}
<Box mb={3} pt={3} sx={{ borderTop: '1px solid #f1f5f9' }}>
  <Typography sx={sectionTitleSx}>Amenities</Typography>

  <Stack spacing={0.25}>
    {visibleAmenities.map((amenity) => (
      <FormControlLabel
        key={amenity}
        sx={checkboxRowSx}
        control={
          <Checkbox
            sx={checkboxSx}
            checked={filters.amenities.includes(amenity)}
            onChange={e => handleAmenityChange(amenity, e.target.checked)}
          />
        }
        label={amenity}
      />
    ))}
  </Stack>

  {amenitiesList.length > 8 && (
    <Button
      size="small"
      onClick={() => setShowAllAmenities(!showAllAmenities)}
      sx={{ mt: 1, textTransform: 'none', color: BLUE, fontWeight: 700 }}
    >
      {showAllAmenities ? 'Show Less' : '+ More'}
    </Button>
  )}
</Box>

        {/* Rating */}
      <Box pt={3} sx={{ borderTop: '1px solid #f1f5f9' }}>
        <Typography sx={sectionTitleSx}>Rating</Typography>
        <Stack spacing={1}>
          {[5, 4, 3, 2].map((rating) => (
            <Button
              key={rating}
              variant={filters.ratings === rating ? 'contained' : 'outlined'}
              startIcon={<Star size={16} fill="#fbbf24" color="#fbbf24" />}
              onClick={() => handleRatingChange(rating)}
              fullWidth
              size="small"
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                borderRadius: '12px',
                fontWeight: 700,
                py: 0.75,
                ...(filters.ratings === rating
                  ? { bgcolor: BLUE, boxShadow: '0 4px 12px rgba(0,102,204,0.25)', '&:hover': { bgcolor: '#004c99' } }
                  : { color: '#334155', borderColor: '#e2e8f0', '&:hover': { bgcolor: '#eff6ff', borderColor: '#bfdbfe' } }),
              }}
            >
              {rating}+ stars
            </Button>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}
